import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EditIcon, UndoIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Modal de edição de metadados de uma CARGA de expedição inteira
 * (todos os contentores com a mesma placa/pedido).
 * Também permite REVERTER (desfazer) o despacho — admin only, apenas EM_TRANSITO.
 */
export default function CargaEditModal({ open, carga, clientes = [], canRevert = false, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    if (carga) {
      setForm({
        cliente: carga.cliente || "",
        no_pedido: carga.no_pedido || "",
        nf_produto: carga.nf_produto || "",
        motorista: carga.motorista || "",
        cpf_motorista: carga.cpf_motorista || "",
        placa: carga.placa || "",
      });
    }
  }, [carga]);

  const submit = async () => {
    if (!carga) return;
    setBusy(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => {
        const v = form[k];
        if (v === "" || v === null || v === undefined) return;
        if (v !== (carga[k] || "")) payload[k] = v;
      });
      if (!Object.keys(payload).length) {
        toast.info("Nenhuma alteração para salvar");
        setBusy(false);
        return;
      }
      const { data } = await api.patch(`/expedicao/carga/${encodeURIComponent(carga.placa)}`, payload);
      toast.success(`Carga atualizada · ${data.contentores_atualizados} contentor(es)`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doRevert = async () => {
    if (!carga) return;
    if (!window.confirm(`Reverter a carga da placa ${carga.placa}? Todos os contentores voltarão para CHEIO e a expedição será desfeita.`)) return;
    setReverting(true);
    try {
      const { data } = await api.post(`/expedicao/carga/${encodeURIComponent(carga.placa)}/reverter`);
      toast.success(`${data.contentores_revertidos} contentor(es) voltaram a CHEIO`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReverting(false);
    }
  };

  if (!carga) return null;
  const isEmTransito = (carga.contentores || []).some((c) => c.status === "EM_TRANSITO");
  const showRevert = canRevert && isAdmin && isEmTransito;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="carga-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <EditIcon className="w-5 h-5 text-[#FFD60A]" /> Editar Carga · <span className="font-mono text-[#FFD60A]">{carga.placa}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Ajustes se propagam para os <b>{(carga.contentores || []).length}</b> contentor(es) desta carga e para o histórico de ciclos.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Cliente" full>
            {clientes.length > 0 ? (
              <select
                data-testid="carga-cliente"
                value={form.cliente}
                onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                className="w-full rounded-none bg-[#0A0A0C] border border-border h-10 px-2 text-sm"
              >
                <option value="">— selecione —</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.nome}>{c.nome}{c.cnpj ? ` · ${c.cnpj}` : ""}</option>
                ))}
                {form.cliente && !clientes.some((c) => c.nome === form.cliente) && (
                  <option value={form.cliente}>{form.cliente} (atual)</option>
                )}
              </select>
            ) : (
              <Input data-testid="carga-cliente" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} className="rounded-none bg-[#0A0A0C] h-10" />
            )}
          </Field>
          <Field label="N° do Pedido">
            <Input data-testid="carga-pedido" value={form.no_pedido} onChange={(e) => setForm({ ...form, no_pedido: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="NF do Produto">
            <Input data-testid="carga-nf" value={form.nf_produto} onChange={(e) => setForm({ ...form, nf_produto: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Motorista">
            <Input data-testid="carga-motorista" value={form.motorista} onChange={(e) => setForm({ ...form, motorista: e.target.value })} className="rounded-none bg-[#0A0A0C] h-10" />
          </Field>
          <Field label="CPF do Motorista">
            <Input data-testid="carga-cpf" value={form.cpf_motorista} onChange={(e) => setForm({ ...form, cpf_motorista: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Placa" full>
            <Input data-testid="carga-placa" value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
        </div>

        <DialogFooter className="mt-2 gap-2">
          {showRevert && (
            <Button
              onClick={doRevert}
              disabled={reverting || busy}
              variant="outline"
              data-testid="carga-revert"
              className="rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 mr-auto"
              title="Desfazer despacho — contentores voltam a CHEIO"
            >
              <UndoIcon className="w-4 h-4 mr-1" /> {reverting ? "Revertendo..." : "Reverter Despacho"}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-none border-border">Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="carga-submit" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 font-bold uppercase tracking-wider">
            {busy ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
